import { Component, Input, OnInit } from "@angular/core";
import {
  AllowedProp,
  AllowedPropType,
  ALLOWED_PROPS_MEDICATION_COMPOUND,
  ALLOWED_PROPS_MEDICATION_NON_COMPOUND,
  ALLOWED_PROPS_OE,
  ALLOWED_PROPS_OM,
  ALLOWED_PROPS_VISIT_FREQUENCY_DECLINED,
  DefaultValueFn,
} from "configs/om-details-config";
import {
  Action,
  ComponentNames,
  DropdownItem,
  Order,
  OrderTypeConfiguration,
  OrderVisitFrequency,
} from "models/order-management/models";
import { StatefulOrderResponse } from "models/order-management/statefullOrderResponse";
import Utils from "utils/Utilities";

type OrderRow = AllowedProp & {
  value: string;
  showMorePNotes: boolean;
};

@Component({
  selector: "om-details",
  templateUrl: "om-details.html",
})
export class OMDetailsComponent implements OnInit {
  @Input() order: StatefulOrderResponse;
  @Input() orderTypeConfigurations: Array<OrderTypeConfiguration> = [];
  @Input() type: string = "";
  rowsGroupedInArraysOfFive = [];
  reasonsForGIP: DropdownItem[];
  removeHome = false;

  constructor() {}

  ngOnInit(): void {
    this.setReasonsForGIP();
    this.showProperties(this.order);
  }

  getRows(order: StatefulOrderResponse, labels: AllowedPropType): OrderRow[] {
    const orderRows = [];
    for (const key in labels) {
      if (labels.hasOwnProperty(key)) {
        const property = key as keyof StatefulOrderResponse;
        const valueOrDefault = this.getValueOrDefault(order, labels, property);
        let { value, skip, isDefault } = valueOrDefault;
        if (skip) {
          continue;
        }

        const { label, position, capitalizeValue } = labels[property];
        let { size } = labels[property];

        if (label === "Patient Location") {
          value = Utils.transformAddress(value);
        }

        if (label === "Care Location" && value === "Home") {
          this.removeHome = true;
          continue;
        }

        if (
          label === "Frequency" &&
          !isDefault &&
          order.orderType === "VISIT_FREQUENCY"
        ) {
          const frequencyDescription =
            OrderVisitFrequency.getIntervalDescription(
              this.order.timeInterval,
              value
            );
          // Only transform frequency if it is a known interval
          if (frequencyDescription !== "") {
            value = `Every ${frequencyDescription}`;
          }
        }

        if (label === "Duration" && !isDefault) {
          value = `${value} ${OrderVisitFrequency.getIntervalDescription(
            // For backwards compatibility: new orders will always have a durationInterval but old ones use the timeInterval
            this.order.durationInterval ?? this.order.timeInterval,
            value
          )}`;
        }

        if (label === "Diagnosis Type") {
          value = `${value} Diagnosis`;
        }

        if (label === "Body Systems") {
          // Empty Body Systems -> "-"
          value =
            !value || value.length === 0
              ? "No Details Entered (via User)"
              : value;
        }

        if (label === "Reason for Non-Coverage") {
          if (order.hospicePays) {
            continue;
          }
          value = this.transformCoverageDesignationCode(value);
        }

        const min = labels[property].min;
        const trueOrFalse = value === true || value === false;
        if (label && (value || trueOrFalse)) {
          orderRows.push({
            label,
            value: value.toString(),
            size,
            position,
            showMorePNotes: false,
            min,
            capitalizeValue,
          });
        }
      }
    }
    return orderRows;
  }

  addEmptyRow(orderRows, size) {
    orderRows.push({
      label: " ",
      value: "            ",
      size: size,
      position: 0,
      showMorePNotes: false,
    });
  }

  sortRowsByPosition(orderRows) {
    return orderRows.sort((a, b) => b.position - a.position);
  }

  groupRowsIntoArrays(sortedRows) {
    const groupSize = this.getGroupSizeByOrderType();
    const rowsGrouped = [];

    let remainingRows = [...sortedRows];

    /*
      it checks if order is MEDICATION if true use a different layout for the details 
      if not then uses the old layout for the other order types. Rerence ULT-2196
    */
    if (this.order.orderType === "MEDICATION") {
      let index: number;

      // Row 1
      rowsGrouped.push(remainingRows.slice(0, 5));
      remainingRows = remainingRows.slice(5);

      // Optional Row 2 for "Compound" Medications
      if (this.order.isCompound) {
        rowsGrouped.push(remainingRows.slice(0, 1));
        remainingRows = remainingRows.slice(1);
      }

      // Use either "Drug Format" or "Non-eRx Administration Format" based on orderSubtype
      const labelToRemove =
        this.order.orderSubtype === "ERX"
          ? "Non-eRx Administration Format"
          : "Drug Format";
      index = remainingRows.findIndex((col) => col.label === labelToRemove);
      if (index !== -1) {
        remainingRows.splice(index, 1);
      }
    }

    for (let i = 0; i < remainingRows.length; i += groupSize) {
      rowsGrouped.push(remainingRows.slice(i, i + groupSize));
    }

    return rowsGrouped;
  }

  getGroupSizeByOrderType() {
    let groupSize: number;
    switch (this.order.orderType) {
      case "DME":
        groupSize = 3;
        break;
      case "VISIT_FREQUENCY":
        groupSize = 4;
        break;
      default:
        groupSize = 5;
    }
    return groupSize;
  }

  showProperties(order: StatefulOrderResponse) {
    const orderType = order.orderType;

    let labels: AllowedPropType;
    if (orderType == "MEDICATION") {
      let isCompound = order.isCompound;

      if (isCompound) {
        labels = ALLOWED_PROPS_MEDICATION_COMPOUND[orderType];
      } else {
        labels = ALLOWED_PROPS_MEDICATION_NON_COMPOUND[orderType];
      }
    } else {
      labels =
        this.type === "OE"
          ? ALLOWED_PROPS_OE[orderType]
          : ALLOWED_PROPS_OM[orderType];
    }
    // Default
    if (!labels) {
      labels =
        this.type === "OE" ? ALLOWED_PROPS_OE.OTHER : ALLOWED_PROPS_OM.OTHER;
    }

    // Decline - if declined after the order is created, all fields are included, otherwise just the decline fields
    if (Order.isVisitFrequencyDeclined(order)) {
      const declineLabels = ALLOWED_PROPS_VISIT_FREQUENCY_DECLINED[orderType];
      if (this.wasDeclinedAfterCreate(order)) {
        labels = { ...declineLabels, ...labels };
      } else {
        labels = declineLabels;
      }
    }

    if (this.order.levelOfCare === "continuousCare") {
      labels = {
        ...labels,
        levelOfCareSymptoms: {
          label: "Symptoms",
          size: this.type === "OE" ? 20 : 20,
          position: 8,
          defaultValue: "No Details Entered (via User)",
        },
      };
    } else if (this.order.levelOfCare === "respiteCare") {
      labels = {
        ...labels,
        reasonForRespite: {
          label: "Reason For Respite",
          size: this.type === "OE" ? 20 : 20,
          position: 8,
        },
      };
    } else if (this.order.levelOfCare === "generalInPatient") {
      labels = {
        ...labels,
        reasonsForGIP: {
          label: "Reason For GIP",
          size: this.type === "OE" ? 20 : 20,
          position: 8,
        },
      };
    }

    // If the order has an 'approvalType' of "written", we do not want to show
    // the "readBackAndVerified" field in the details, so we delete it from the
    // 'labels' object
    if (this.order.approvalType === "written") {
      labels = {
        ...labels,
      };
      delete labels["readBackAndVerified"];
    }

    // If the order does not have a PRN, we do not want to show the
    // "PRNQuantity" or "PRNReasons" fields in the details, so we delete them
    // from the 'labels' object
    if (!this.order.PRN) {
      labels = {
        ...labels,
      };
      delete labels["PRN"];
      delete labels["PRNQuantity"];
      delete labels["PRNReasons"];
    }

    // Add keys if undefined
    order.categories = order.categories === undefined ? null : order.categories;

    let orderRows = this.getRows(order, labels);
    const sortedRows = this.sortRowsByPosition(orderRows);
    this.rowsGroupedInArraysOfFive = this.groupRowsIntoArrays(sortedRows);
    this.addEmptyRows(orderType, this.rowsGroupedInArraysOfFive);
  }

  addEmptyRows(orderType, rowsOfFive) {
    switch (orderType) {
      case "MEDICATION":
        this.addEmptyRow(
          rowsOfFive[rowsOfFive.length - 1],
          this.type === "OE" ? 20 : 22
        );
        break;
      case "DME":
        if (this.removeHome) {
          this.addEmptyRow(rowsOfFive[0], 22);
        }
        this.addEmptyRow(rowsOfFive[1], this.type === "OE" ? 22 : 22);
        if (rowsOfFive[0].length > 3) {
          this.addEmptyRow(rowsOfFive[1], this.type === "OE" ? 22 : 22);
        }
        break;
      case "VISIT_FREQUENCY":
        this.addEmptyRow(rowsOfFive[1], this.type === "OE" ? 20 : 44);
        break;
    }
  }

  public isBooleanValue(value: string) {
    return ["Hospice Coverage", "PRN", "Read Back & Verified"].includes(value);
  }
  public isOrderEnterDate(value: string) {
    return value === "Order Enter Date";
  }
  public isBodySystemCategory(value: string) {
    return value === "Body Systems";
  }
  public isAbbreviatedTextArea(value: string) {
    return ["Provider Notes", "Order Instructions", "Order Notes"].includes(
      value
    );
  }
  public isDiscontinueDate(value: string) {
    return value === "Discontinue Date";
  }
  public isVisits(value: string) {
    return value === "Visits";
  }
  public isGIP(value: string) {
    return value === "Reason For GIP";
  }
  public isLocSymptoms(value: string) {
    return value === "Symptoms";
  }
  public isIngredients(value: string) {
    return value === "Ingredients";
  }
  public isRoutineHomeCareOM() {
    return this.order.levelOfCare === "routineHomeCare" && this.type === "OM";
  }
  public isPRNReasons(value: string) {
    return value === "PRN Reason";
  }
  public isIssue(value: string) {
    return value === "Issue";
  }
  public isDeclinedDate(value: string) {
    return value === "Date Declined";
  }

  public isPatientLocationPhoneNumber(value: string) {
    return value === "Phone Number";
  }

  public isPatientLocationFaxNumber(value: string) {
    return value === "Fax Number";
  }

  setReasonsForGIP() {
    const component = this.orderTypeConfigurations
      .find((o) => o.orderType === this.order.orderType)
      .components?.find((c) => c.componentName === ComponentNames.reasonForGIP);
    if (component) {
      this.reasonsForGIP = component.properties[0].dropdownItems;
    }
  }

  getReasonsForGIP(reasons: string[]) {
    let displayReasons = [];
    for (const element of reasons) {
      const value = this.reasonsForGIP.find(({ value }) => value === element);
      if (value) {
        displayReasons.push(
          value.label +
            " " +
            (value.labelDescription
              ? value.labelDescription
              : this.order.reasonsForGIPOther
              ? this.order.reasonsForGIPOther
              : "")
        );
      }
    }
    return displayReasons.join("\n");
  }

  trimString(string, length) {
    return string.length > length
      ? string.substring(0, length) + "..."
      : string;
  }

  getClasses(row) {
    return {
      "no-margin-label": true,
      "capitalizing-label": !!(
        row.capitalizeValue === undefined || row.capitalizeValue
      ),
    };
  }
  reformatDataCyAttribute(attribute: string) {
    return attribute.replace(/\s+/g, "-").toLowerCase();
  }

  wasDeclinedAfterCreate(order: StatefulOrderResponse) {
    return order.history.some(
      (history) => history.actionType === Action.ActionTypeEnum.Declined
    );
  }

  transformCoverageDesignationCode(value: string): string {
    // Locate the configuration that has the matching 'orderType' property
    const componentOrderType = this.orderTypeConfigurations.find(
      (config) => config.orderType === this.order.orderType
    );

    // Check if 'componentOrderType' is not 'undefined' - if so, assign value to
    // variable, otherwise assign 'undefined' value
    const orderTypeComponents = componentOrderType
      ? componentOrderType.components
      : undefined;

    // If the variable is 'undefined', return the 'value' parameter
    if (!orderTypeComponents) {
      return value;
    }

    // Locate the component that has a 'properties' field with a populated array
    // of objects where one of those objects has a 'name' field with a value of
    // "coverageDesignation" - if found, assign the component to the variable
    const componentReasonForNonCoverage = orderTypeComponents.find(
      (component) =>
        component.properties &&
        component.properties.some(
          (property) => property.name && property.name === "coverageDesignation"
        )
    );

    // If the variable is 'undefined' or does not have a populated
    // 'dropdownItems' field as part of the first indexed object in the
    // 'properties' field value array return the 'value' parameter
    if (
      !componentReasonForNonCoverage ||
      !componentReasonForNonCoverage.properties[0].dropdownItems
    ) {
      return value;
    }

    // Assign the array of 'dropdownItems' to the variable
    const dropdownItemsCoverageDesignation =
      componentReasonForNonCoverage.properties[0].dropdownItems;

    // If the variable is 'undefined', return the 'label' parameter
    if (!dropdownItemsCoverageDesignation) {
      return value;
    }

    // Locate the 'dropdownItem' whose 'value' field matches the 'value'
    // parameter
    const coverageDesignation = dropdownItemsCoverageDesignation.find(
      (item) => item.value === value
    );

    // If the variable is 'undefined', return the 'value' parameter
    if (!coverageDesignation) {
      return value;
    }

    // Check if the 'value' field is present - if so, assign value to variable,
    // otherwise assign 'undefined' value
    const coverageDesignationLabel = coverageDesignation.label
      ? coverageDesignation.label
      : undefined;

    // If the variable is 'undefined', return the 'value' parameter
    if (!coverageDesignationLabel) {
      return value;
    }

    // Return the 'coverageDesignation' label we are looking for
    return coverageDesignationLabel;
  }

  getValueOrDefault(
    order: StatefulOrderResponse,
    labels: AllowedPropType,
    key: keyof StatefulOrderResponse
  ): {
    value: any;
    skip: boolean;
    isDefault: boolean;
  } {
    // Determine value or default. If no default, property is omitted.
    let value: any;
    let noValue = true;
    let isDefault = false;

    if (key.toString() === "patientLocationPhoneNumber") {
      value = Utils.formatPhoneMobileOrFaxNumber(
        this.order.patientLocation.phoneNumber
      );
      value = value === "-" ? "" : value;
      noValue = false;
    } else if (key.toString() === "patientLocationFaxNumber") {
      value = Utils.formatPhoneMobileOrFaxNumber(
        this.order.patientLocation.faxNumber
      );
      value = value === "-" ? "" : value;
      noValue = false;
    } else if (order.hasOwnProperty(key)) {
      value = order[key];
      noValue = false;
    }

    // If string, trim and check if empty
    if (typeof value === "string" && value.trim() === "") {
      noValue = true;
    }

    // If no value, check the default string or default function
    if (noValue && typeof labels[key].defaultValue === "function") {
      value = (labels[key].defaultValue as DefaultValueFn)(order, key);
      noValue = false;
      isDefault = true;
    } else if (noValue && typeof labels[key].defaultValue === "string") {
      value = labels[key].defaultValue;
      noValue = false;
      isDefault = true;
    }

    return {
      value: noValue ? null : value,
      skip: noValue,
      isDefault,
    };
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/om-details/om-details.ts